import type { CustomRequestConfig } from '@mathflat/domain/@entities/Auth/axiosJwtSetup'
import { jwtTokenService } from '@mathflat/domain/@entities/Auth/jwtTokenService'
import { domainProvider } from '@mathflat/shared/@common/domain/domainProvider'
import axios, { type AxiosInstance, type AxiosResponse, HttpStatusCode } from 'axios'
import qs from 'qs'

import { datadogClient } from '~/@app/init/datadog'
import ServerMaintenanceService from '~/@pages/serverMaintenance/@service/ServerMaintenance.service'

import { TOAST_STRING } from '../constants/strings'
import { errorHandlerService, toastService } from '../services'
import { envUtils } from './envUtils'
import { wrapErrorHandlerWithJwt, wrapRequestInterceptorWithJwt } from './jwtInterceptors'

const PREVENT_TOAST_ERROR_CODES = [
  'STUDENT_WORKSHEET_NOT_FOUND',
  'WORKSHEET_NOT_FOUND',
  'WORKSHEET_ALREADY_DELETED',
  'WORKSHEET_PERMISSION_DENIED',
  'WORKSHEET_PROBLEM_EMPTY',
  'SELF_LEARNING_NOT_COMPLETE',
  'STUDENT_WORKSHEET_NOT_COMPLETE',
  'STUDENT_WORKSHEET_PROBLEM_DUPLICATE_SUBMIT',
  'STUDENT_WORKBOOK_PROBLEM_DUPLICATE_SUBMIT',
  'STUDENT_EXAM_PROBLEM_DUPLICATE_SUBMIT',
  'EXAM_NOT_STARTED',
  'EXAM_START_CLOSED',
  'EXAM_NOT_FOUND',
  'EXAM_NOT_VALID',
  'SELF_LEARNING_ALREADY_EXHAUSTED',
  'SELF_LEARNING_ALREADY_CREATED',
  'USER_WRONG_PASSWORD',
  'DAILY_ASSIGNMENT_TIME_EXCEEDED',
]

const BASE_URL = (() => {
  if (envUtils.isStaging) {
    return `https://api-staging.${domainProvider.TARGET_DOMAIN}`
  }
  if (envUtils.isLive) {
    return `https://api.${domainProvider.TARGET_DOMAIN}`
  }
  return localStorage.getItem('DEV_API') ?? `https://api-dev.${domainProvider.TARGET_DOMAIN}`
})()

/**
 * AxiosInstance를 확장하여 response.data를 바로 반환하는 타입
 */
export interface MaxiosInstance extends AxiosInstance {
  request<T = any, R = AxiosResponse<T>['data'], D = any>(
    config: CustomRequestConfig<D>,
  ): Promise<R>
  get<T = any, R = AxiosResponse<T>['data'], D = any>(
    url: string,
    config?: CustomRequestConfig<D>,
  ): Promise<R>
  delete<T = any, R = AxiosResponse<T>['data'], D = any>(
    url: string,
    config?: CustomRequestConfig<D>,
  ): Promise<R>
  head<T = any, R = AxiosResponse<T>['data'], D = any>(
    url: string,
    config?: CustomRequestConfig<D>,
  ): Promise<R>
  options<T = any, R = AxiosResponse<T>['data'], D = any>(
    url: string,
    config?: CustomRequestConfig<D>,
  ): Promise<R>
  post<T = any, R = AxiosResponse<T>['data'], D = any>(
    url: string,
    data?: D,
    config?: CustomRequestConfig<D>,
  ): Promise<R>
  put<T = any, R = AxiosResponse<T>['data'], D = any>(
    url: string,
    data?: D,
    config?: CustomRequestConfig<D>,
  ): Promise<R>
  patch<T = any, R = AxiosResponse<T>['data'], D = any>(
    url: string,
    data?: D,
    config?: CustomRequestConfig<D>,
  ): Promise<R>
  postForm<T = any, R = AxiosResponse<T>['data'], D = any>(
    url: string,
    data?: D,
    config?: CustomRequestConfig<D>,
  ): Promise<R>
  putForm<T = any, R = AxiosResponse<T>['data'], D = any>(
    url: string,
    data?: D,
    config?: CustomRequestConfig<D>,
  ): Promise<R>
  patchForm<T = any, R = AxiosResponse<T>['data'], D = any>(
    url: string,
    data?: D,
    config?: CustomRequestConfig<D>,
  ): Promise<R>
}

const maxios: MaxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    common: {
      'x-platform': 'STUDENT',
      'x-freewheelin-host': envUtils.isSchool ? 'schoolflat.com' : domainProvider.TARGET_DOMAIN,
    },
    post: { 'Content-Type': 'application/json' },
  },
  paramsSerializer: {
    serialize: (params) => {
      return qs.stringify(params, { arrayFormat: 'comma' })
    },
  },
})

maxios.interceptors.request.use(wrapRequestInterceptorWithJwt((config) => config))

maxios.interceptors.response.use(
  (response) => response.data,
  wrapErrorHandlerWithJwt(
    async (error: unknown) => {
      datadogClient.sendError(error as Error)

      // 인터넷 연결을 보장하지 못함 (https://developer.mozilla.org/en-US/docs/Web/API/Navigator/onLine)
      // navigator.onLine이 undefined일 수 있어서 false와 비교
      if (navigator.onLine === false) {
        toastService.error(TOAST_STRING.networkOffline)
        return Promise.reject(error)
      }

      const axiosError = error as { response?: { status?: number; data?: { code?: string } } }
      const status = axiosError?.response?.status
      const errorCode = axiosError?.response?.data?.code

      if (!status) {
        errorHandlerService.handle(error as Error)
        return Promise.reject(error)
      }

      if (status === HttpStatusCode.NotFound) {
        return error
      }
      if (status >= 501) {
        await ServerMaintenanceService.checkServerMaintenance()
        return error
      }

      errorHandlerService.handle(error as Error, {
        logType: 'error',
        useRemoteLogging: false,
        preventToast:
          status === HttpStatusCode.BadRequest &&
          !!errorCode &&
          PREVENT_TOAST_ERROR_CODES.includes(errorCode),
      })

      return Promise.reject(error)
    },
    async (originalRequest, newAccessToken) => {
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
      return maxios(originalRequest)
    },
  ),
)

export default maxios
